import { CircleCheckBig } from 'lucide-react';
import { useState } from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/shadcn/ui/input-group';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/shadcn/ui/toggle-group';
import { withForm } from '@/shared/ui/form';
import { getFieldError } from '@/shared/ui/form/get-field-error';

import { useSignalSelectOptions } from '../../hooks';
import { CONDITION_OPERATOR, signalAlertFormOptions } from '../../model';

type SignalMode = 'all' | 'specific';

export const SignalAlertTriggerSection = withForm({
  ...signalAlertFormOptions,
  props: {
    workspaceId: '',
  },
  render: function SignalAlertTriggerSection({ form, workspaceId }) {
    const { options } = useSignalSelectOptions({ workspaceId });

    // 초기 signals 값으로 모드 결정 (빈 배열 = all issues)
    const [signalMode, setSignalMode] = useState<SignalMode>(() =>
      form.state.values.signals.length > 0 ? 'specific' : 'all',
    );
    const [durationInputValue, setDurationInputValue] = useState<string>(() =>
      String(form.state.values.duration),
    );
    const [isDurationFocused, setIsDurationFocused] = useState(false);

    const getDurationValidationMessage = (value: string) => {
      const num = Number(value);
      if (num < 1) return 'Duration must be at least 1 day';
      if (num > 7) return 'Duration must be at most 7 days';
      return undefined;
    };

    const handleModeChange = (mode: SignalMode) => {
      if (!mode) return;
      setSignalMode(mode);
      if (mode === 'all') {
        form.setFieldValue('signals', []);
      }
    };

    return (
      <div className="flex flex-col gap-5 rounded-lg border p-5">
        {/* 헤더 */}
        <div className="flex items-center gap-2">
          <CircleCheckBig className="size-5 text-green-600" />
          <span className="text-sm font-semibold">Trigger</span>
        </div>

        {/* Signal 선택 */}
        <div className="grid grid-cols-[100px_1fr] items-start gap-3">
          <span className="pt-2 text-sm text-muted-foreground">Signal</span>
          <div className="flex flex-col gap-2">
            <ToggleGroup
              type="single"
              value={signalMode}
              variant="outline"
              size="sm"
              className="w-fit"
              onValueChange={(value) => handleModeChange(value as SignalMode)}
            >
              <ToggleGroupItem value="all">All issues</ToggleGroupItem>
              <ToggleGroupItem value="specific">
                Specific signals
              </ToggleGroupItem>
            </ToggleGroup>
            {signalMode === 'specific' && (
              <form.AppField
                name="signals"
                children={(field) => (
                  <field.ComboboxTagInput
                    options={options}
                    placeholder="Select signal"
                    searchPlaceholder="Search signals..."
                    emptyMessage="No signals found"
                  />
                )}
              />
            )}
          </div>
        </div>

        {/* Filter */}
        <div className="grid grid-cols-[100px_1fr] items-center gap-3">
          <span className="text-sm text-muted-foreground">Filter</span>
          <form.AppField
            name="filter"
            children={(field) => <field.StructuredFilter />}
          />
        </div>

        {/* Duration */}
        <div className="grid grid-cols-[100px_1fr] items-start gap-3">
          <span className="pt-2 text-sm text-muted-foreground">Duration</span>
          <form.AppField
            name="duration"
            validators={{
              onChange: ({ value }) => {
                const num = Number(value);
                if (num < 1) return 'Duration must be at least 1 day';
                if (num > 7) return 'Duration must be at most 7 days';
                return undefined;
              },
              onSubmit: ({ value }) => {
                const num = Number(value);
                if (num < 1) return 'Duration must be at least 1 day';
                if (num > 7) return 'Duration must be at most 7 days';
                return undefined;
              },
            }}
            children={(field) => (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">Last</span>
                  <InputGroup className="w-20">
                    <InputGroupInput
                      type="number"
                      min={1}
                      max={7}
                      value={
                        isDurationFocused
                          ? durationInputValue
                          : String(field.state.value ?? '')
                      }
                      onFocus={() => {
                        setDurationInputValue(String(field.state.value ?? ''));
                        setIsDurationFocused(true);
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDurationInputValue(value);

                        const num = Number(value);
                        if (value === '' || Number.isNaN(num)) return;
                        if (num < 1 || num > 7) return;

                        field.handleChange(num);
                      }}
                      onBlur={() => {
                        setDurationInputValue(String(field.state.value ?? ''));
                        setIsDurationFocused(false);
                        field.handleBlur();
                      }}
                    />
                  </InputGroup>
                  <span className="text-sm">days</span>
                </div>
                {(getDurationValidationMessage(durationInputValue) ||
                  field.state.meta.errors.length > 0) && (
                  <span className="text-destructive text-sm">
                    {getDurationValidationMessage(durationInputValue) ??
                      getFieldError(field.state.meta.errors[0])}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Trigger when */}
        <div className="grid grid-cols-[100px_1fr] items-start gap-3">
          <span className="pt-2 text-sm text-muted-foreground">
            Trigger when
          </span>
          <div className="flex flex-col gap-3">
            {/* Feedback count condition */}
            <div className="flex items-center gap-2">
              <form.AppField
                name="feedbackCountCondition.enabled"
                children={(field) => (
                  <field.Checkbox text="Feedback count exceeds" />
                )}
              />
              <form.AppField
                name="feedbackCountCondition.threshold"
                children={(field) => (
                  <InputGroup className="w-24">
                    <field.InputGroupInput
                      type="number"
                      min={0}
                      showErrorMessage={false}
                    />
                  </InputGroup>
                )}
              />
            </div>

            {/* And / Or 토글 */}
            <form.Subscribe
              selector={(state) =>
                state.values.changeRateCondition.enabled && // 모두 활성화 되어야 operator 사용 가능
                state.values.feedbackCountCondition.enabled
              }
            >
              {(enabled) => (
                <form.AppField
                  name="conditionOperator"
                  children={(field) => (
                    <field.ToggleGroup
                      options={[
                        { value: CONDITION_OPERATOR.AND, label: 'And' },
                        { value: CONDITION_OPERATOR.OR, label: 'Or' },
                      ]}
                      size="sm"
                      className="w-fit"
                      disabled={!enabled}
                    />
                  )}
                />
              )}
            </form.Subscribe>

            {/* Change rate condition */}
            <div className="flex items-center gap-2">
              <form.AppField
                name="changeRateCondition.enabled"
                children={(field) => (
                  <field.Checkbox text="Change rate increases by" />
                )}
              />
              <form.AppField
                name="changeRateCondition.threshold"
                children={(field) => (
                  <InputGroup className="w-20">
                    <field.InputGroupInput
                      type="number"
                      min={0}
                      max={100}
                      showErrorMessage={false}
                    />
                    <InputGroupAddon align="inline-end">
                      <InputGroupText>%</InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
});
